import React, { useEffect, useState } from "react";
import AppWrapper from "../AppWrapper";
import {
  Badge,
  BlockStack,
  Button,
  Card,
  IndexTable,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
  Divider,
} from "@shopify/polaris";
import { useNavigate, useParams } from "react-router-dom";
import supabase from "../../supabase-client";
import { formatFriendlyDate, formatShortDate, formatPrice, numberWithCommas, formatPlate } from "../../utility/funcs";
import InsuranceStatusBadge from "../Insurance/InsuranceStatusBadge";
import StatusBadge from "../Services/StatusBadge";
import { PageDownIcon, PageUpIcon } from "@shopify/polaris-icons";
import moment from "moment";
import CarMap from "./CarMap";
import CarEgresosCard from "../Egresos/CarEgresosCard";
import UploadFilesModal from "../CustomComponents/UploadFilesModal/UploadFilesModal";
import { UserAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";

// 🧮 Helper to compute upcoming services
function getNextServices(car, serviceTypes) {
  if (!car?.odometer || !serviceTypes) return [];

  const currentOdo = car.odometer;
  const lastServices = car.last_services || {};

  return serviceTypes
    .map((type) => {
      const last = lastServices[type.id];
      const interval = type.interval || 0;

      if (last) {
        const kmsSince = currentOdo - last.odo;
        const kmsRemaining = interval - kmsSince;

        return {
          id: type.id,
          name: type.name,
          lastDate: last.date,
          lastOdo: last.odo,
          kmsSince,
          kmsRemaining,
          predicted: false,
        };
      } else {
        // Service never done — predict based on odometer
        const kmsSince = currentOdo;
        const kmsRemaining = interval - currentOdo;

        return {
          id: type.id,
          name: type.name,
          lastDate: null,
          lastOdo: null,
          kmsSince,
          kmsRemaining,
          predicted: true,
        };
      }
    })
    .sort((a, b) => a.kmsRemaining - b.kmsRemaining);
}

// 💡 Component for displaying next services
function CarNextServices({ car, serviceTypes }) {
  const next = getNextServices(car, serviceTypes);

  if (!next.length)
    return (
      <Card title="Próximos servicios" sectioned>
        <Text>No hay servicios próximos registrados.</Text>
      </Card>
    );

  return (
    <Card title="Próximos servicios" sectioned>
      <BlockStack gap={300}>
        <Text variant="headingMd">Próximos servicios</Text>
        <BlockStack gap={200}>
          {next.map((s) => {
            let tone = "success";
            if (s.kmsRemaining <= 1000) tone = "critical";
            else if (s.kmsRemaining <= 3000) tone = "attention";

            return (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text>
                  {s.name}
                  {s.predicted && (
                    <span style={{ color: "#888", fontSize: "0.8em" }}>
                      {" "}
                      (estimado – nunca realizado)
                    </span>
                  )}
                  <br />
                  {s.lastDate ? (
                    <span style={{ fontSize: "0.8em", color: "#888" }}>
                      Último a {s.lastOdo.toLocaleString()} km (
                      {new Date(s.lastDate).toLocaleDateString("es-MX")})
                    </span>
                  ) : (
                    <span style={{ fontSize: "0.8em", color: "#888" }}>
                      Aún no realizado
                    </span>
                  )}
                </Text>

                <Badge tone={tone}>
                  {s.kmsRemaining > 0
                    ? `${s.kmsRemaining.toLocaleString()} km`
                    : `${Math.abs(s.kmsRemaining).toLocaleString()} km vencido`}
                </Badge>
              </div>
            );
          })}
        </BlockStack>
      </BlockStack>
    </Card>
  );
}

function Car() {
  const { carId } = useParams();
  const navigate = useNavigate();
  const { hasPermission, session } = UserAuth();
  const { branches } = useBranch();

  const [car, setCar] = useState(undefined);
  const [insurance, setInsurance] = useState(undefined);
  const [serviceTypes, setServiceTypes] = useState(undefined);
  const [pastServices, setPastServices] = useState(undefined);

  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [isDownloadingKeychain, setIsDownloadingKeychain] = useState(false);
  const [serviceBlockModal, setServiceBlockModal] = useState(false);
  const [serviceBlockStart, setServiceBlockStart] = useState("");
  const [serviceBlockEnd, setServiceBlockEnd] = useState("");
  const [serviceBlockNotes, setServiceBlockNotes] = useState("");
  const [serviceBlockSaving, setServiceBlockSaving] = useState(false);

  const [transferModal, setTransferModal] = useState(false);
  const [transferBranchId, setTransferBranchId] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferSaving, setTransferSaving] = useState(false);
  const [branchHistory, setBranchHistory] = useState([]);
  const [plateHistory, setPlateHistory] = useState([]);

  const loadServices = async (carId) => {
    const { data } = await supabase
      .from("services")
      .select(
        "id, performed_at, odometer, status, total, notes, provider:service_providers(name), items:service_items(type:service_types(name))"
      )
      .eq("car", carId)
      .order("performed_at", { ascending: false })
      .order("id", { ascending: false });
    setPastServices(data || []);
  };

  const loadPlateHistory = async (carId) => {
    const { data } = await supabase
      .from("car_plate_history")
      .select("id, from_plate, to_plate, changed_at, user:users!changed_by(name)")
      .eq("car_id", carId)
      .order("changed_at", { ascending: false });
    if (data) setPlateHistory(data);
  };

  const loadBranchHistory = async (carId) => {
    const { data } = await supabase
      .from("car_branch_history")
      .select(
        "id, from_branch_id, to_branch_id, reason, changed_at, from_branch:branches!from_branch_id(name), to_branch:branches!to_branch_id(name), user:users!changed_by(name)"
      )
      .eq("car_id", carId)
      .order("changed_at", { ascending: false });
    if (data) setBranchHistory(data);
  };

  const saveTransfer = async () => {
    if (!transferBranchId) return;
    const toId = parseInt(transferBranchId);
    if (toId === car.branch_id) {
      alert("El auto ya está en esa sucursal");
      return;
    }
    setTransferSaving(true);
    // Atomic transfer: RPC updates cars.branch_id (bypasses the
    // prevent-direct-change trigger via a session flag) and writes the
    // history row in the same transaction.
    const { error: rpcErr } = await supabase.rpc("transfer_car_branch", {
      p_car_id: car.id,
      p_to_branch_id: toId,
      p_reason: transferReason || null,
      p_changed_by: session?.user?.id || null,
    });
    if (rpcErr) {
      alert("Error: " + rpcErr.message);
      setTransferSaving(false);
      return;
    }
    setCar({ ...car, branch_id: toId });
    setTransferModal(false);
    setTransferBranchId("");
    setTransferReason("");
    setTransferSaving(false);
    loadBranchHistory(car.id);
  };

  const createServiceBlock = async () => {
    setServiceBlockSaving(true);
    // App convention: store wallclock-local times tagged +00 so they read back
    // face-value (no UTC conversion). toISOString() would shift by 6 hours.
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const nowStamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}+00`;
    // Start: chosen date at 08:00 if user picked one; else "now" (current second).
    const startStamp = serviceBlockStart ? `${serviceBlockStart} 08:00:00+00` : nowStamp;
    if (serviceBlockEnd && serviceBlockStart && serviceBlockEnd < serviceBlockStart) {
      alert("La fecha de liberación no puede ser anterior a la fecha de inicio.");
      setServiceBlockSaving(false);
      return;
    }
    const endDate = serviceBlockEnd ? `${serviceBlockEnd} 18:00:00+00` : null;
    const dateRange = endDate ? `[${startStamp},${endDate}]` : `[${startStamp},)`;

    // Guard: car must be 100% free during the service block window. Refuse if
    // ANY active reservation (rental OR service) overlaps the proposed range.
    // Open-ended service blocks (no end date) check from start forward.
    const overlapRange = endDate ? `[${startStamp},${endDate}]` : `[${startStamp},infinity)`;
    const { data: conflicts, error: checkErr } = await supabase
      .from("reservations")
      .select("id, type, date_range")
      .eq("car", car.id)
      .eq("status", "active")
      .overlaps("date_range", overlapRange);
    if (checkErr) {
      alert("Error verificando disponibilidad: " + checkErr.message);
      setServiceBlockSaving(false);
      return;
    }
    // A live apartado (car_holds) also counts: someone promised this car.
    const { data: holdConflicts } = await supabase
      .from("car_holds")
      .select("id, label")
      .eq("car_id", car.id)
      .is("released_at", null)
      .is("converted_at", null)
      .gt("expires_at", new Date().toISOString())
      .overlaps("date_range", overlapRange);
    if ((conflicts && conflicts.length > 0) || holdConflicts?.length) {
      const list = [
        ...(conflicts || []).map((c) => `#${c.id} (${c.type})`),
        ...(holdConflicts || []).map((h) => `apartado${h.label ? ` de ${h.label}` : ""}`),
      ].join(", ");
      alert(
        `No se puede bloquear el auto: existen reservaciones activas que se traslapan con el periodo de servicio: ${list}`
      );
      setServiceBlockSaving(false);
      return;
    }

    const { error } = await supabase.from("reservations").insert({
      type: "service",
      car: car.id,
      branch_id: car.branch_id,
      status: "active",
      date_range: dateRange,
      rent_time: { days: 0, hours: 0 },
      totals: { notes: serviceBlockNotes },
      insurance: "none",
      total_price: 0,
      balance: 0,
      deposit: 0,
      created_by: session?.user?.id || null,
    });

    if (error) {
      alert("Error: " + error.message);
      setServiceBlockSaving(false);
      return;
    }

    setServiceBlockModal(false);
    setServiceBlockStart("");
    setServiceBlockEnd("");
    setServiceBlockNotes("");
    setServiceBlockSaving(false);
    alert("Auto bloqueado por servicio");
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch car
      const { data: carData, error: carError } = await supabase
        .from("cars")
        .select("*, model(name, image_url, show_as, brand(name))")
        .eq("id", carId)
        .single();

      if (carError) {
        console.error(carError);
        return;
      }
      setCar(carData);
      loadBranchHistory(carData.id);
      loadPlateHistory(carData.id);

      const { data: inData, error: inError } = await supabase
        .from("insurance")
        .select("*, provider(name)")
        .eq("car", carId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (inError) {
        setInsurance(false);
      } else {
        setInsurance(inData);
      }

      // Fetch service types (intervals)
      const { data: stData, error: stError } = await supabase
        .from("service_types")
        .select("id, name, interval, show_on_report")
        .not("interval", "is", null);

      if (stError) {
        console.error(stError);
      }
      setServiceTypes(stData);
    };
    loadFiles();
    loadServices(carId);

    if (!car || !serviceTypes) fetchData();
  }, [carId]);

  const carDetailCard = () => {
    return (
      <Card>
        <InlineGrid columns={2} alignItems="center" gap={300}>
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "right",
            }}
          >
            <img
              alt={car.model.name}
              src={car.model.image_url}
              style={{ width: "200px", height: "200px", alignSelf: "center" }}
            />
          </div>
          <BlockStack align="center">
            <Text variant="headingMd" tone="subdued">
              {car.vin}
            </Text>
            <Text variant="headingLg">{formatPlate(car.plate)}</Text>
            <Text variant="headingMd" tone="subdued">
              {car.model.brand.name} | {car.model.name}
            </Text>
            <Text variant="headingMd" tone="subdued">
              {numberWithCommas(car.odometer)} km
            </Text>
          </BlockStack>
        </InlineGrid>
      </Card>
    );
  };

  const downloadInsurance = async () => {
    const { data, error } = await supabase.storage
      .from("polizas")
      .getPublicUrl(`${insurance.document}.pdf`);
    if (error) {
      console.error(error);
      return;
    }
    window.open(data.publicUrl, "_blank");
  };

  const insuranceDetail = () => {
    const insruanceCardContent = () => {
      if (insurance === undefined) {
        return <Spinner size="small" />;
      }
      if (insurance === false) {
        return (
          <div>
            <Badge tone="critical">Sin Seguro</Badge>
          </div>
        );
      }
      return (
        <BlockStack gap={100}>
          <Text>{insurance?.provider?.name}</Text>
          <Text>
            Poliza:{" "}
            {insurance.is_temporary ? "CARTA COBERTURA" : insurance?.policy_no}
          </Text>
          <div>
            <Text variant="subdued">
              Desde: {formatFriendlyDate(insurance?.validity_from)}
            </Text>
            <Text variant="subdued">
              Hasta: {formatFriendlyDate(insurance?.validity_to)}
            </Text>
          </div>
        </BlockStack>
      );
    };

    return (
      <Card>
        {insurance && (
          <div style={{ position: "absolute", right: "1rem", bottom: "1rem" }}>
            <Button icon={PageDownIcon} onClick={downloadInsurance} />
          </div>
        )}

        <BlockStack>
          <InlineStack align="space-between">
            <Text variant="headingMd">Póliza de Seguro</Text>

            {/* ✅ FIX: Solo mostrar si insurance NO es undefined */}
            {insurance !== undefined && (
              <InsuranceStatusBadge status={insurance?.status} />
            )}
          </InlineStack>

          {insruanceCardContent()}
        </BlockStack>
      </Card>
    );
  };

  const odometerVerificationBadge = () => {
    const verified_at = car?.odometer_verified_at;
    if (!verified_at) return <Badge tone="attention">Verificar Odometro</Badge>;

    if (moment().diff(moment(verified_at), "days") > 21)
      return <Badge tone="attention">Verificar Odometro</Badge>;
    return <></>;
  };

  async function loadFiles() {
    setLoadingFiles(true);

    const { data, error } = await supabase.storage
      .from("car-docs")
      .list(`${carId}/`, { limit: 50 });

    if (error) {
      console.error(error);
      setFiles([]);
      setLoadingFiles(false);
      return;
    }

    // Generate signed URLs for each file
    const filePromises = data.map(async (f) => {
      const { data: signed } = await supabase.storage
        .from("car-docs")
        .createSignedUrl(`${carId}/${f.name}`, 60 * 60);
      return { ...f, signedUrl: signed?.signedUrl };
    });

    const final = await Promise.all(filePromises);
    setFiles(final);
    setLoadingFiles(false);
  }

  const documentsDetail = () => {
    return (
      <Card title="Documentos del Vehículo">
        <BlockStack gap={300}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Text variant="headingMd">Documentos</Text>
            {hasPermission("cars.docs.create") ? (
              <div style={{ marginLeft: "auto" }}>
                <Button
                  onClick={() => setUploadModalOpen(true)}
                  icon={PageUpIcon}
                />
              </div>
            ) : (
              <></>
            )}
          </div>
          {loadingFiles ? (
            <Spinner />
          ) : files.length === 0 ? (
            <Text>No hay documentos subidos.</Text>
          ) : (
            <BlockStack gap={200}>
              {files.map((f) => (
                <InlineStack key={f.name} align="space-between">
                  <Text>{f.name}</Text>
                  <Button url={f.signedUrl} external size="slim">
                    Ver
                  </Button>
                </InlineStack>
              ))}
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    );
  };

  const serviceHistoryDetail = () => {
    return (
      <Card>
        <BlockStack gap="300">
          <Text variant="headingMd">Historial de Servicios</Text>
          {pastServices === undefined ? (
            <Spinner size="small" />
          ) : pastServices.length === 0 ? (
            <Text tone="subdued">No hay servicios registrados para este auto.</Text>
          ) : (
            <IndexTable
              resourceName={{ singular: "servicio", plural: "servicios" }}
              selectable={false}
              itemCount={pastServices.length}
              headings={[
                { title: "Fecha" },
                { title: "Odómetro" },
                { title: "Tipo / Notas" },
                { title: "Proveedor" },
                { title: "Total" },
                { title: "Estado" },
              ]}
            >
              {pastServices.map((s, i) => {
                const types = [
                  ...new Set(
                    (s.items || []).map((it) => it.type?.name).filter(Boolean)
                  ),
                ].join(", ");
                return (
                  <IndexTable.Row
                    id={String(s.id)}
                    key={s.id}
                    position={i}
                    onClick={() => navigate(`/services/${s.id}`)}
                  >
                    <IndexTable.Cell>{formatShortDate(s.performed_at)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {s.odometer != null ? `${numberWithCommas(s.odometer)} km` : "—"}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Text variant="bodySm" truncate>{types || s.notes || "—"}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{s.provider?.name || "—"}</IndexTable.Cell>
                    <IndexTable.Cell>{formatPrice(s.total)}</IndexTable.Cell>
                    <IndexTable.Cell><StatusBadge status={s.status} /></IndexTable.Cell>
                  </IndexTable.Row>
                );
              })}
            </IndexTable>
          )}
        </BlockStack>
      </Card>
    );
  };

  const pageContent = () => {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <InlineGrid columns={["twoThirds", "oneThird"]} gap={300}>
              {carDetailCard()}
              {insuranceDetail()}
            </InlineGrid>
          </Layout.Section>

          <Layout.Section>
            <InlineGrid columns={["twoThirds", "oneThird"]} gap={300}>
              <div>
                <CarNextServices car={car} serviceTypes={serviceTypes} />
              </div>
              <CarMap vin={car?.vin} />
            </InlineGrid>
          </Layout.Section>
          <Layout.Section>{serviceHistoryDetail()}</Layout.Section>
          <Layout.Section>
            <CarEgresosCard carId={carId} />
          </Layout.Section>
          <Layout.Section>{documentsDetail()}</Layout.Section>
          {plateHistory.length > 0 && (
            <Layout.Section>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Historial de Placa</Text>
                  {plateHistory.map((h, i) => (
                    <div key={h.id}>
                      {i > 0 && <Divider />}
                      <div style={{ padding: "8px 0" }}>
                        <Text variant="bodyMd" fontWeight="semibold">
                          {h.from_plate || "—"} → {h.to_plate}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                          {formatFriendlyDate(h.changed_at)}
                          {h.user?.name ? ` · ${h.user.name}` : ""}
                        </Text>
                      </div>
                    </div>
                  ))}
                </BlockStack>
              </Card>
            </Layout.Section>
          )}
          {branchHistory.length > 0 && (
            <Layout.Section>
              <Card>
                <BlockStack gap="200">
                  <Text variant="headingMd">Historial de Sucursal</Text>
                  {branchHistory.map((h, i) => (
                    <div key={h.id}>
                      {i > 0 && <Divider />}
                      <div style={{ padding: "8px 0" }}>
                        <Text variant="bodyMd" fontWeight="semibold">
                          {h.from_branch?.name || "—"} → {h.to_branch?.name}
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                          {formatFriendlyDate(h.changed_at)}
                          {h.user?.name ? ` · ${h.user.name}` : ""}
                          {h.reason ? ` · ${h.reason}` : ""}
                        </Text>
                      </div>
                    </div>
                  ))}
                </BlockStack>
              </Card>
            </Layout.Section>
          )}
        </Layout>
      </Page>
    );
  };


  function downloadKeychain(isCopy) {
    setIsDownloadingKeychain(true);
    fetch("http:///localhost:9001/generate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        plate: car.plate,
        model: car.model.name,
        isCopy,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const errorBody = await res.text();
          console.error("API error:", errorBody);
          throw new Error(errorBody);
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = isCopy ? `${car.plate || "sin-placa"}_copy.3mf` : `${car.plate || "sin-placa"}.3mf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        setIsDownloadingKeychain(false);
      })

      .catch((err) => {
        console.error(err);
        alert("Failed to generate file");
        setIsDownloadingKeychain(false);
      });
  }

  const actionGroups = () => {
    var actionGroup = [
      {
        title: "Descargar Llavero",
        disabled: isDownloadingKeychain,
        actions: [
          {
            content: "Original",
            onAction: () => downloadKeychain(false),
          },
          {
            content: "Copia",
            onAction: () => downloadKeychain(true),
          },
        ],
      },
    ];
    return actionGroup;
  };

  const mainAction = () => {
    if (hasPermission("cars.edit")) {
      return {
        content: "Editar",
        onAction: () => {
          navigate(`/cars/edit?id=${car.id}`);
        },
      };
    }
  };

  return (
    <AppWrapper>
      <Page
        backAction={{
          onAction: () => {
            navigate(-1);
          },
        }}
        actionGroups={actionGroups()}
        primaryAction={mainAction()}
        secondaryActions={car ? [
          {
            content: "Bloquear por Servicio",
            onAction: () => setServiceBlockModal(true),
          },
          {
            content: "Transferir Sucursal",
            onAction: () => {
              setTransferBranchId("");
              setTransferReason("");
              setTransferModal(true);
            },
          },
          {
            content: car?.active === false ? "Activar" : "Desactivar",
            tone: car?.active === false ? undefined : "critical",
            onAction: async () => {
              const newActive = !car.active;
              const { error } = await supabase
                .from("cars")
                .update({ active: newActive })
                .eq("id", car.id);
              if (error) {
                alert("Error: " + error.message);
                return;
              }
              setCar({ ...car, active: newActive });
            },
          },
        ] : []}
        title={!car ? "" : `${car.model.name} (${formatPlate(car.plate)})`}
        subtitle={!car ? "" : `${car.vin}`}
        titleMetadata={
          <InlineStack gap="200">
            {odometerVerificationBadge()}
            {car && car.active === false && <Badge tone="critical">Inactivo</Badge>}
          </InlineStack>
        }
      >
        {!car ? <Spinner size="small" /> : pageContent()}
        {uploadModalOpen && (
          <UploadFilesModal
            carId={carId}
            onClose={() => setUploadModalOpen(false)}
            onUploaded={() => loadFiles()}
          />
        )}
        <Modal
          open={transferModal}
          onClose={() => setTransferModal(false)}
          title="Transferir a otra Sucursal"
          primaryAction={{
            content: "Transferir",
            onAction: saveTransfer,
            loading: transferSaving,
            disabled: !transferBranchId,
          }}
          secondaryActions={[
            { content: "Cancelar", onAction: () => setTransferModal(false) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              {car && (
                <Text variant="bodySm" tone="subdued">
                  Sucursal actual:{" "}
                  {branches.find((b) => b.id === car.branch_id)?.name || "—"}
                </Text>
              )}
              <Select
                label="Nueva sucursal"
                value={transferBranchId}
                options={[
                  { label: "Seleccionar", value: "" },
                  ...branches
                    .filter((b) => b.id !== car?.branch_id)
                    .map((b) => ({ label: b.name, value: String(b.id) })),
                ]}
                onChange={setTransferBranchId}
              />
              <TextField
                label="Motivo (opcional)"
                value={transferReason}
                onChange={setTransferReason}
                placeholder="Ej: Reubicación operativa"
                autoComplete="off"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
        <Modal
          open={serviceBlockModal}
          onClose={() => setServiceBlockModal(false)}
          title="Bloquear por Servicio"
          primaryAction={{
            content: "Bloquear",
            onAction: createServiceBlock,
            loading: serviceBlockSaving,
          }}
          secondaryActions={[
            { content: "Cancelar", onAction: () => setServiceBlockModal(false) },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="300">
              <Text variant="bodySm" tone="subdued">
                El auto no estará disponible para rentar durante el periodo del bloqueo.
              </Text>
              <TextField
                label="Inicio del bloqueo"
                type="date"
                value={serviceBlockStart}
                onChange={setServiceBlockStart}
                helpText="Dejar vacío para iniciar ahora mismo"
              />
              <TextField
                label="Fecha estimada de liberación (opcional)"
                type="date"
                value={serviceBlockEnd}
                onChange={setServiceBlockEnd}
                helpText="Dejar vacío para bloqueo abierto"
              />
              <TextField
                label="Notas"
                value={serviceBlockNotes}
                onChange={setServiceBlockNotes}
                multiline={2}
                placeholder="Motivo del bloqueo, tipo de servicio, etc."
              />
            </BlockStack>
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}
export default Car;
